import { parse, format } from "date-fns";
import * as XLSX from "xlsx";

/**
 * Export calls to Excel file
 * @param {Array} calls - Array of call objects
 * @param {string} metricType - Type of metric (incomplete, today, tomorrow, etc.)
 */
export const exportCallsToExcel = (calls, metricType) => {
  if (!calls || calls.length === 0) {
    console.warn("No calls to export");
    return;
  }

  // Map call objects to spreadsheet rows
  const rows = calls.map((call) => ({
    Project: call.project?.name || call.projectName || "-",
    Location: call.location?.name || call.locationName || "-",
    "Location #": call.location?.locationNumber || "-",
    "Street Address":
      call.location?.address?.streetAddress || call.streetAddress || "-",
    Frequency: formatFrequency(call.frequency),
    City: call.location?.address?.city || "-",
    Province: call.location?.address?.province || "-",
    Rep: call.repName || "-",
    "Scheduled Date": formatDate(call.yyyymmdd),
    Status: call.callStatus || "-",
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Calls");

  // Style header row
  const headerCellStyle = {
    font: { bold: true, color: "FFFFFF" },
    fill: { fgColor: { rgb: "4472C4" } },
    alignment: { horizontal: "center", vertical: "center" },
  };

  // Apply header styling
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const address = XLSX.utils.encode_col(C) + "1";
    if (!ws[address]) continue;
    ws[address].s = headerCellStyle;
  }

  // Adjust column widths
  const columnWidths = [
    { wch: 20 }, // Project
    { wch: 20 }, // Location
    { wch: 12 }, // Location #
    { wch: 28 }, // Street Address
    { wch: 28 }, // Frequency
    { wch: 15 }, // City
    { wch: 12 }, // Province
    { wch: 15 }, // Rep
    { wch: 18 }, // Scheduled Date
    { wch: 12 }, // Status
  ];
  ws["!cols"] = columnWidths;

  // Generate filename with timestamp
  const timestamp = format(new Date(), "yyyy-MM-dd_HHmmss");
  const filename = `calls-${metricType}-${timestamp}.xlsx`;

  // Trigger download
  XLSX.writeFile(wb, filename);
};

/**
 * Generic export to Excel
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Filename without extension
 */
export const exportToExcel = (data, filename) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");

  // Set column widths based on content
  const cols = Object.keys(data[0]).map((key) => {
    const maxLength = Math.max(
      key.length,
      ...data.map((row) => String(row[key] || "").length),
    );
    return { wch: Math.min(maxLength + 2, 50) };
  });
  ws["!cols"] = cols;

  // Generate filename with extension
  const sanitizedFilename = filename.replace(/[^a-z0-9_-]/gi, "_");
  const fullFilename = `${sanitizedFilename}.xlsx`;

  // Trigger download
  XLSX.writeFile(wb, fullFilename);
};

/**
 * Format date from yyyy-MM-dd string to readable format
 * @param {string} dateStr - Date in yyyy-MM-dd format
 * @returns {string} Formatted date (e.g., "Monday, January 28")
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const parsedDate = parse(dateStr, "yyyy-MM-dd", new Date());
    return format(parsedDate, "EEEE, MMMM d");
  } catch (err) {
    return dateStr;
  }
};

const formatFrequency = (frequency) => {
  if (!frequency || typeof frequency !== "object") return "-";

  const weekLabels = ["week1", "week2", "week3", "week4", "week5"]
    .map((weekKey, index) => {
      const value = String(frequency?.[weekKey] || "").trim();
      if (!value) return null;
      return `W${index + 1}: ${value}`;
    })
    .filter(Boolean);

  return weekLabels.length > 0 ? weekLabels.join(" | ") : "-";
};
